// Version 10.3 - BrowserRouter implementation - 2025-12-08
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import AnnouncementBar from './components/AnnouncementBar.jsx';
import Navigation from './components/Navigation.jsx';
import Hero from './components/Hero.jsx';
import BodyWisdomQuote from './components/BodyWisdomQuote.jsx';
import About from './components/About.jsx';
import Services from './components/Services.jsx';
import ExpertQuotes from './components/ExpertQuotes.jsx';
import HowItWorks from './components/HowItWorks.jsx';
import Testimonials from './components/Testimonials.jsx';
import FAQ from './components/FAQ.jsx';
import Booking from './components/Booking.jsx';
import Footer from './components/Footer.jsx';
import ForProfessionals from './components/ForProfessionals.jsx';
import FAQSchema from './components/FAQSchema.jsx';
import Location from './components/Location.jsx';
import BoundaryService from './pages/BoundaryService.jsx';
import GroupCoachingService from './pages/GroupCoachingService.jsx';
import PeoplePleasingTherapy from './pages/PeoplePleasingTherapy.jsx';
import IndividualSessions from './pages/IndividualSessionsNew.jsx';
import WorkbookMain from './pages/WorkbookMain.jsx';
import WorkbookPreview from './pages/WorkbookPreview.jsx';
import WorkbookGetStarted from './pages/WorkbookGetStarted.jsx';
import WorkbookAbout from './pages/WorkbookAbout.jsx';
import WorkbookComingSoon from './pages/WorkbookComingSoon.jsx';
import AboutPage from './pages/AboutPage.jsx';
import MyApproachPage from './pages/MyApproachPageNew.jsx';
import AboutMePage from './pages/AboutMePage.jsx';
import TherapyRelationshipPage from './pages/TherapyRelationshipPage.jsx';
import LeadMagnetPage from './pages/LeadMagnetPage.jsx';
import ThankYouPage from './pages/ThankYouPage.jsx';
import PrivacyPolicy from './pages/PrivacyPolicy.jsx';
import TermsConditions from './pages/TermsConditions.jsx';
import FAQPage from './pages/FAQPage.jsx';
import ContactPage from './pages/ContactPage.jsx';
import BlogPage from './pages/BlogPage.jsx';
import BlogPeoplePleasingCoping from './pages/BlogPeoplePleasingCoping.jsx';
import BlogNervousSystemSafety from './pages/BlogNervousSystemSafety.jsx';
import BlogBreakingTheCycle from './pages/BlogBreakingTheCycle.jsx';
import BlogEmotionalNeglect from './pages/BlogEmotionalNeglect.jsx';
import BlogCoachingVsTherapy from './pages/BlogCoachingVsTherapy.jsx';
import BlogDeeperThanBoundaries from './pages/BlogDeeperThanBoundaries.jsx';
import BlogWhenRestFeelsUnsafe from './pages/BlogWhenRestFeelsUnsafe.jsx';
import WaitlistSignup from './pages/WaitlistSignup.jsx';
import PathOneEnrollment from './pages/PathOneEnrollment.jsx';
import TherapistConsultation from './pages/TherapistConsultation.jsx';
import MomsCircle from './pages/MomsCircle.jsx';
import SouthAsianSisters from './pages/SouthAsianSisters.jsx';
import TestimonialsPage from './pages/TestimonialsPage.jsx';

// Scroll to top component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}


function HomePage({ scrolled }) {
  return (
    <div className="min-w-full overflow-x-hidden bg-cream-50">
      <FAQSchema />
      <Hero />
      <About />
      <Services />
      <HowItWorks />
      <ExpertQuotes />
      {/* FAQ and Contact moved to separate pages - accessible via navigation */}
      {/* <FAQ /> */}
      {/* <Location /> */}
      {/* <Booking /> */}
    </div>
  );
}

function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Legal pages with their own Navigation/Footer */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
        
        {/* HomePage with its own structure */}
        <Route path="/" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="transparent" scrolled={scrolled} />
            <HomePage scrolled={scrolled} />
            <Footer />
          </div>
        } />
        
        {/* About pages */}
        <Route path="/about" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation scrolled={scrolled} />
            <div className="h-12"></div>
            <div className="h-12"></div>
            <AboutPage />
            <Footer />
          </div>
        } />
        <Route path="/my-approach" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <div className="h-12"></div>
            <MyApproachPage />
            <Footer />
          </div>
        } />
        <Route path="/about-me" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="transparent" />
            <div className="h-12"></div>
            <div className="h-12"></div>
            <AboutMePage />
            <Footer />
          </div>
        } />

        {/* Service pages */}
        <Route path="/services/individual-sessions" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="transparent" />
            <div className="h-12"></div>
            <IndividualSessions />
            <Footer />
          </div>
        } />
        <Route path="/services/group-coaching" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="transparent" />
            <div className="h-12"></div>
            <GroupCoachingService />
            <Footer />
          </div>
        } />
        <Route path="/services/moms-circle" element={<MomsCircle />} />
        <Route path="/services/south-asian-sisters" element={<SouthAsianSisters />} />
        <Route path="/services/therapist-consultation" element={<Navigate to="/services/individual-sessions" replace />} />
        {/* Old routes - kept for backwards compatibility */}
        <Route path="/services/boundary-coaching" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="dark" />
            <div className="h-12"></div>
            <BoundaryService />
            <Footer />
          </div>
        } />
        <Route path="/services/people-pleasing-therapy" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="dark" />
            <div className="h-12"></div>
            <PeoplePleasingTherapy />
            <Footer />
          </div>
        } />
        
        {/* Other pages */}
        <Route path="/therapy-relationship" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation scrolled={true} />
            <div className="h-12"></div>
            <TherapyRelationshipPage />
            <Footer />
          </div>
        } />
        
        <Route path="/free-guide" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="transparent" />
            <div className="h-12"></div>
            <LeadMagnetPage />
            <Footer />
          </div>
        } />
        
        <Route path="/thank-you" element={<ThankYouPage />} />
        
        {/* Testimonials page */}
        <Route path="/testimonials" element={<TestimonialsPage />} />

        {/* FAQ, Contact, and Blog pages */}
        <Route path="/faq" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <FAQPage />
            <Footer />
          </div>
        } />
        <Route path="/contact" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <ContactPage />
            <Footer />
          </div>
        } />
        <Route path="/blog" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogPage />
            <Footer />
          </div>
        } />
        <Route path="/blog/nervous-system-safety" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogNervousSystemSafety />
            <Footer />
          </div>
        } />
        <Route path="/blog/breaking-the-cycle" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogBreakingTheCycle />
            <Footer />
          </div>
        } />
        <Route path="/blog/people-pleasing-coping-mechanism" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogPeoplePleasingCoping />
            <Footer />
          </div>
        } />
        <Route path="/blog/emotional-neglect" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogEmotionalNeglect />
            <Footer />
          </div>
        } />
        <Route path="/blog/coaching-vs-therapy" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogCoachingVsTherapy />
            <Footer />
          </div>
        } />
        <Route path="/blog/deeper-than-boundaries" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogDeeperThanBoundaries />
            <Footer />
          </div>
        } />
        <Route path="/blog/when-rest-feels-unsafe" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation variant="white" />
            <BlogWhenRestFeelsUnsafe />
            <Footer />
          </div>
        } />
        
        {/* CCM Program Pages */}
        <Route path="/waitlist-signup" element={<WaitlistSignup />} />
        <Route path="/path-one-enrollment" element={<PathOneEnrollment />} />
        
        {/* Workbook coming soon */}
        <Route path="/workbook-coming-soon" element={<WorkbookComingSoon />} />
        
        {/* Workbook pages */}
        <Route path="/workbook" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation scrolled={true} />
            <div className="h-12"></div>
            <WorkbookMain />
            <Footer />
          </div>
        } />
        <Route path="/workbook/preview" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation scrolled={true} />
            <div className="h-12"></div>
            <WorkbookPreview />
            <Footer />
          </div>
        } />
        <Route path="/workbook/get-started" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation scrolled={true} />
            <div className="h-12"></div>
            <WorkbookGetStarted />
            <Footer />
          </div>
        } />
        <Route path="/workbook/about" element={
          <div className="min-w-full overflow-x-hidden">
            <AnnouncementBar />
            <Navigation scrolled={true} />
            <div className="h-12"></div>
            <WorkbookAbout />
            <Footer />
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
